// src/components/dashboard/CalendarIntegration.js
import { AlertCircle, Calendar, CheckCircle, Copy, Download, ExternalLink, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useBusiness } from '../../hooks/useBusiness';
import { FirebaseService } from '../../services/FirebaseService';
import { GoogleCalendarAPIService } from '../../services/GoogleCalendarAPIService';

const CalendarIntegration = () => {
  const { user } = useAuth();
  const { businessProfile } = useBusiness();
  const [calendarStatus, setCalendarStatus] = useState('loading');
  const [calendarData, setCalendarData] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);
  const [copySuccess, setCopySuccess] = useState('');

  useEffect(() => {
    if (businessProfile && user?.businessId) {
      checkCalendarStatus();
    }
  }, [businessProfile, user]);

  const withSubscriptionUrls = (sharingInfo, calendarId) => {
    const resolvedCalendarId = calendarId || sharingInfo.calendarId;
    const subscriptionUrls = resolvedCalendarId
      ? GoogleCalendarAPIService.generateSubscriptionURLs(
          resolvedCalendarId,
          businessProfile?.businessName || 'My Business'
        )
      : null;
    return { ...sharingInfo, subscriptionUrls };
  };

  const checkCalendarStatus = async () => {
    try {
      setError(null);
      const status = await FirebaseService.getCalendarIntegrationStatus(user.businessId);
      setCalendarStatus(status.status);

      if (status.status === 'active') {
        const sharingInfo = await FirebaseService.getCalendarSharingInfo(user.businessId);
        if (sharingInfo.success) {
          setCalendarData(withSubscriptionUrls(sharingInfo, status.calendarId));
        }
      }
    } catch (error) {
      setError('Failed to check calendar status');
      setCalendarStatus('error');
      console.error('Calendar status check failed:', error);
    }
  };

  const createCalendar = async () => {
  setIsCreating(true);
  setError(null);
  
  try {
    console.log('🗓️ Creating calendar for business:', user.businessId);
    const result = await FirebaseService.createBusinessCalendar(user.businessId, businessProfile);
    
    if (result.success) {
      console.log('✅ Calendar operation successful:', result);
      
      // Handle both new creation and existing calendar scenarios
      if (result.alreadyExisted) {
        console.log('📅 Calendar already existed, using existing calendar');
        // Show a different message for existing calendars
      } else {
        console.log('🆕 New calendar created successfully');
      }
      
      setCalendarStatus('active');
      
      // Get updated sharing info
      const sharingInfo = await FirebaseService.getCalendarSharingInfo(user.businessId);
      if (sharingInfo.success) {
        setCalendarData(withSubscriptionUrls(sharingInfo));
      }
      
      // Instead of full page reload, just refresh the business profile
      // This is lighter than window.location.reload()
      // window.location.reload(); // Comment out for now
      
    } else {
      setError(result.error || 'Failed to create calendar');
      setCalendarStatus('error');
    }
  } catch (error) {
    console.error('❌ Calendar creation error:', error);
    setError(error.message || 'Calendar creation failed');
    setCalendarStatus('error');
  } finally {
    setIsCreating(false);
  }
};

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(type);
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const renderCalendarStatus = () => {
    switch (calendarStatus) {
      case 'loading':
        return (
          <div className="flex items-center">
            <RefreshCw className="h-5 w-5 animate-spin text-blue-600 mr-2" />
            <span className="text-gray-600">Checking calendar status...</span>
          </div>
        );

      case 'missing':
        return (
          <div>
            <div className="flex items-center mb-4">
              <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
              <span className="text-gray-900 font-medium">Google Calendar Not Set Up</span>
            </div>
            
            <p className="text-gray-600 mb-4">
              Your Google Calendar integration is not set up yet. This will allow customers to see your availability and receive calendar invites for their appointments.
            </p>
            
            <button
              onClick={createCalendar}
              disabled={isCreating}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
            >
              {isCreating ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  Creating Calendar...
                </>
              ) : (
                <>
                  <Calendar className="h-4 w-4 mr-2" />
                  Create Google Calendar
                </>
              )}
            </button>
            
            {error && (
              <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                <p className="font-medium">Setup Failed</p>
                <p className="text-sm mt-1">{error}</p>
                <button
                  onClick={createCalendar}
                  className="mt-2 text-red-700 underline text-sm hover:text-red-800"
                >
                  Try Again
                </button>
              </div>
            )}
          </div>
        );

      case 'active':
        return (
          <div>
            <div className="flex items-center mb-4">
              <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
              <span className="text-gray-900 font-medium">Google Calendar Active</span>
            </div>
            
            <p className="text-gray-600 mb-6">
              Your business calendar is set up and ready to use. Share it with customers or embed it on your website.
            </p>

            {calendarData && (
              <div className="space-y-6">
                {/* Public Calendar Link */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Public Calendar Link
                  </label>
                  <div className="flex">
                    <input
                      type="text"
                      value={calendarData.publicUrl}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg bg-gray-50 text-sm"
                    />
                    <button
                      onClick={() => copyToClipboard(calendarData.publicUrl, 'url')}
                      className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 hover:bg-gray-200 rounded-r-lg"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                  {copySuccess === 'url' && (
                    <p className="text-green-600 text-xs mt-1">✓ Link copied to clipboard</p>
                  )}
                  <div className="mt-2 flex space-x-3">
                    <a
                      href={calendarData.publicUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 text-sm hover:text-blue-700 flex items-center"
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      Open Calendar
                    </a>
                    <a
                      href={calendarData.icsUrl}
                      className="text-blue-600 text-sm hover:text-blue-700 flex items-center"
                    >
                      <Download className="h-3 w-3 mr-1" />
                      Download .ics
                    </a>
                  </div>
                </div>

                {/* Subscription Links */}
                {calendarData.subscriptionUrls && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subscribe to Calendar
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Add to your calendar app to see bookings update automatically.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <a
                        href={calendarData.subscriptionUrls.appleCalendarURL}
                        className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
                      >
                        🍎 Apple Calendar
                      </a>
                      <a
                        href={calendarData.subscriptionUrls.outlookSubscription}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        Add to Outlook
                      </a>
                      <button
                        onClick={() => copyToClipboard(calendarData.subscriptionUrls.iCalURL, 'ical')}
                        className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        Copy iCal URL
                      </button>
                    </div>
                    {copySuccess === 'ical' && (
                      <p className="text-green-600 text-xs mt-1">✓ iCal URL copied to clipboard</p>
                    )}
                  </div>
                )}

                {/* Embed Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website Embed Code
                  </label>
                  <div className="flex">
                    <textarea
                      value={calendarData.embedCode}
                      readOnly
                      rows={3}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg bg-gray-50 text-xs font-mono resize-none"
                    />
                    <button
                      onClick={() => copyToClipboard(calendarData.embedCode, 'embed')}
                      className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 hover:bg-gray-200 rounded-r-lg self-start"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                  {copySuccess === 'embed' && (
                    <p className="text-green-600 text-xs mt-1">✓ Embed code copied to clipboard</p>
                  )}
                  <p className="text-gray-500 text-xs mt-1">
                    Paste this code on your website to show your calendar
                  </p>
                </div>

                {/* Calendar Preview */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Calendar Preview
                  </label>
                  <div className="border border-gray-300 rounded-lg overflow-hidden">
                    <iframe
                      src={calendarData.publicUrl}
                      style={{ border: 0 }}
                      width="100%"
                      height="400"
                      frameBorder="0"
                      scrolling="no"
                      title="Business Calendar Preview"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'error':
        return (
          <div>
            <div className="flex items-center mb-4">
              <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
              <span className="text-gray-900 font-medium">Calendar Integration Error</span>
            </div>
            
            <p className="text-gray-600 mb-4">
              There was an issue with your calendar integration. Please try again or contact support if the problem persists.
            </p>
            
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                <p className="text-sm">{error}</p>
              </div>
            )}
            
            <div className="flex space-x-3">
              <button
                onClick={checkCalendarStatus}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Check Status
              </button>
              
              <button
                onClick={createCalendar}
                disabled={isCreating}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {isCreating ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                    Retrying...
                  </>
                ) : (
                  <>
                    <Calendar className="h-4 w-4 mr-2" />
                    Retry Setup
                  </>
                )}
              </button>
            </div>
          </div>
        );

      default:
        return (
          <div className="text-gray-500">
            Unknown calendar status. Please refresh the page.
          </div>
        );
    }
  };

  if (!businessProfile) {
    return (
      <div className="bg-white p-6 rounded-lg shadow border">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow border">
      <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
        <Calendar className="h-5 w-5 mr-2 text-blue-600" />
        Google Calendar Integration
      </h3>
      
      {renderCalendarStatus()}
    </div>
  );
};

export default CalendarIntegration;